import { boxesOverlap, type Box3, type Vec3 } from "./math3d.js";

export type BodyType3D = "static" | "kinematic" | "dynamic";

export interface Body3D {
  owner?: unknown;
  type: BodyType3D;
  /** Center in world space. */
  position: Vec3;
  /** Full extents; the box runs half of each either side of the center. */
  size: Vec3;
  velocity: Vec3;
  gravityScale?: number;
  restitution?: number;
  /** Ground friction per second while resting on something. */
  friction?: number;
  /** Overlaps are reported, never resolved. */
  sensor?: boolean;
  layer?: number;
  mask?: number;
  /** Set by the solver each step. */
  onGround?: boolean;
}

export interface Contact3D {
  a: Body3D;
  b: Body3D;
  normal: Vec3;
  depth: number;
}

export interface World3DOptions {
  gravity?: Vec3;
  cellSize?: number;
  sleepSpeed?: number;
}

const DEFAULT_CELL = 4;
const DEFAULT_SLEEP = 0.05;

export function boxOfBody(body: Body3D): Box3 {
  const h = { x: body.size.x / 2, y: body.size.y / 2, z: body.size.z / 2 };
  return {
    min: { x: body.position.x - h.x, y: body.position.y - h.y, z: body.position.z - h.z },
    max: { x: body.position.x + h.x, y: body.position.y + h.y, z: body.position.z + h.z },
  };
}

export function layersInteract3(a: Body3D, b: Body3D): boolean {
  const aLayer = a.layer ?? 1;
  const bLayer = b.layer ?? 1;
  const aMask = a.mask ?? 0xffffffff;
  const bMask = b.mask ?? 0xffffffff;
  return (aMask & bLayer) !== 0 && (bMask & aLayer) !== 0;
}

/**
 * An axis-aligned 3D physics world.
 *
 * The same shape as the 2D one and for the same reasons: AABB only, one axis
 * resolved at a time, separation along the axis of least penetration. In 3D
 * the axis-at-a-time rule matters more, not less — a character walking into a
 * wall while falling has overlaps on two axes at once, and resolving them
 * together launches it along the diagonal.
 *
 * A hash grid rather than a sweep-and-prune, because a grid degrades
 * gracefully: SAP is faster on a scene spread along one axis and pathological
 * on a scene stacked along it, and a game world is usually stacked.
 */
export class World3D {
  gravity: Vec3;
  cellSize: number;
  sleepSpeed: number;

  #bodies: Body3D[] = [];
  /** Broadphase buckets, rebuilt for each axis of each step. */
  #grid = new Map<number, Body3D[]>();
  #span = new Int32Array(6);
  #contacts: Contact3D[] = [];

  constructor(options: World3DOptions = {}) {
    this.gravity = { ...(options.gravity ?? { x: 0, y: -20, z: 0 }) };
    this.cellSize = Math.max(0.1, options.cellSize ?? DEFAULT_CELL);
    this.sleepSpeed = options.sleepSpeed ?? DEFAULT_SLEEP;
  }

  get bodies(): readonly Body3D[] {
    return this.#bodies;
  }

  get contacts(): readonly Contact3D[] {
    return this.#contacts;
  }

  add(body: Body3D): Body3D {
    this.#bodies.push(body);
    return body;
  }

  remove(body: Body3D): boolean {
    const i = this.#bodies.indexOf(body);
    if (i < 0) return false;
    this.#bodies.splice(i, 1);
    return true;
  }

  clear(): void {
    this.#bodies.length = 0;
    this.#contacts.length = 0;
  }

  /** Advance by `dt` seconds. Meant for a fixed-timestep loop. */
  step(dt: number): void {
    this.#contacts.length = 0;

    for (const body of this.#bodies) {
      if (body.type !== "dynamic") continue;
      const scale = body.gravityScale ?? 1;
      body.velocity.x += this.gravity.x * scale * dt;
      body.velocity.y += this.gravity.y * scale * dt;
      body.velocity.z += this.gravity.z * scale * dt;
      body.onGround = false;
    }

    this.#moveAxis(dt, "x");
    this.#moveAxis(dt, "y");
    this.#moveAxis(dt, "z");

    for (const body of this.#bodies) {
      if (body.type !== "dynamic") continue;
      if (body.onGround && body.friction) {
        const keep = Math.max(0, 1 - body.friction * dt);
        body.velocity.x *= keep;
        body.velocity.z *= keep;
      }
      if (Math.abs(body.velocity.x) < this.sleepSpeed) body.velocity.x = 0;
      if (Math.abs(body.velocity.y) < this.sleepSpeed) body.velocity.y = 0;
      if (Math.abs(body.velocity.z) < this.sleepSpeed) body.velocity.z = 0;
    }
  }

  #moveAxis(dt: number, axis: "x" | "y" | "z"): void {
    for (const body of this.#bodies) {
      if (body.type === "static") continue;
      body.position[axis] += body.velocity[axis] * dt;
    }

    this.#rebuildGrid();

    for (const body of this.#bodies) {
      if (body.type !== "dynamic") continue;
      for (const other of this.#near(body)) {
        if (other === body) continue;
        if (!layersInteract3(body, other)) continue;
        if (!boxesOverlap(boxOfBody(body), boxOfBody(other))) continue;

        const contact = contactBetween3(body, other);
        if (!contact) continue;
        this.#contacts.push(contact);
        if (body.sensor || other.sensor) continue;

        const n = contact.normal[axis];
        if (n === 0) continue;
        body.position[axis] += n * contact.depth;
        const bounce = body.restitution ?? 0;
        if (Math.sign(body.velocity[axis]) === -Math.sign(n)) {
          body.velocity[axis] = bounce > 0 ? -body.velocity[axis] * bounce : 0;
        }
        // Pushed upward means something solid is underneath.
        if (axis === "y" && n > 0) body.onGround = true;
      }
    }
  }

  /**
   * Ten bits per axis in one number. A Map of numbers hashes far faster than
   * one of "x,y,z" strings, and this runs for every cell of every body on every
   * axis of every step. Cells 1024 apart share a key; that only hands the
   * narrowphase a candidate it then rejects on overlap.
   */
  #key(x: number, y: number, z: number): number {
    return ((x & 0x3ff) << 20) | ((y & 0x3ff) << 10) | (z & 0x3ff);
  }

  /** The cell range `body` covers, into `#span` as x0, y0, z0, x1, y1, z1. */
  #spanOf(body: Body3D): Int32Array {
    const b = boxOfBody(body);
    const s = this.cellSize;
    const span = this.#span;
    span[0] = Math.floor(b.min.x / s);
    span[1] = Math.floor(b.min.y / s);
    span[2] = Math.floor(b.min.z / s);
    span[3] = Math.floor(b.max.x / s);
    span[4] = Math.floor(b.max.y / s);
    span[5] = Math.floor(b.max.z / s);
    return span;
  }

  #rebuildGrid(): void {
    this.#grid.clear();
    for (const body of this.#bodies) {
      const c = this.#spanOf(body);
      for (let z = c[2]!; z <= c[5]!; z++) {
        for (let y = c[1]!; y <= c[4]!; y++) {
          for (let x = c[0]!; x <= c[3]!; x++) {
            const key = this.#key(x, y, z);
            let bucket = this.#grid.get(key);
            if (!bucket) this.#grid.set(key, (bucket = []));
            bucket.push(body);
          }
        }
      }
    }
  }

  /** Bodies sharing a cell with this one, each once. */
  #near(body: Body3D): Body3D[] {
    const seen = new Set<Body3D>();
    const out: Body3D[] = [];
    const c = this.#spanOf(body);
    for (let z = c[2]!; z <= c[5]!; z++) {
      for (let y = c[1]!; y <= c[4]!; y++) {
        for (let x = c[0]!; x <= c[3]!; x++) {
          const bucket = this.#grid.get(this.#key(x, y, z));
          if (!bucket) continue;
          for (const other of bucket) {
            if (seen.has(other)) continue;
            seen.add(other);
            out.push(other);
          }
        }
      }
    }
    return out;
  }

  /** Every body containing a world point. */
  pointCast(point: Vec3): Body3D[] {
    return this.#bodies.filter((body) => {
      const b = boxOfBody(body);
      return (
        point.x >= b.min.x && point.x <= b.max.x &&
        point.y >= b.min.y && point.y <= b.max.y &&
        point.z >= b.min.z && point.z <= b.max.z
      );
    });
  }

  /**
   * The first body a ray hits.
   *
   * This is what a click in a `Viewport3D` turns into: `camera.screenToRay`
   * gives the ray, and this says what it struck.
   */
  rayCast(
    origin: Vec3,
    direction: Vec3,
    maxDistance = Infinity,
    filter?: (body: Body3D) => boolean,
  ): { body: Body3D; point: Vec3; distance: number } | null {
    let best: { body: Body3D; point: Vec3; distance: number } | null = null;
    for (const body of this.#bodies) {
      if (filter && !filter(body)) continue;
      const hit = raySlab3(origin, direction, boxOfBody(body));
      if (hit === null || hit > maxDistance) continue;
      if (best && hit >= best.distance) continue;
      best = {
        body,
        distance: hit,
        point: {
          x: origin.x + direction.x * hit,
          y: origin.y + direction.y * hit,
          z: origin.z + direction.z * hit,
        },
      };
    }
    return best;
  }
}

/** The overlap between two boxes, along the axis of least penetration. */
export function contactBetween3(a: Body3D, b: Body3D): Contact3D | null {
  const ab = boxOfBody(a);
  const bb = boxOfBody(b);
  const x = Math.min(ab.max.x, bb.max.x) - Math.max(ab.min.x, bb.min.x);
  const y = Math.min(ab.max.y, bb.max.y) - Math.max(ab.min.y, bb.min.y);
  const z = Math.min(ab.max.z, bb.max.z) - Math.max(ab.min.z, bb.min.z);
  if (x <= 0 || y <= 0 || z <= 0) return null;

  if (x <= y && x <= z) {
    return {
      a,
      b,
      normal: { x: a.position.x < b.position.x ? -1 : 1, y: 0, z: 0 },
      depth: x,
    };
  }
  if (y <= z) {
    return {
      a,
      b,
      normal: { x: 0, y: a.position.y < b.position.y ? -1 : 1, z: 0 },
      depth: y,
    };
  }
  return {
    a,
    b,
    normal: { x: 0, y: 0, z: a.position.z < b.position.z ? -1 : 1 },
    depth: z,
  };
}

/** Distance along a ray to a box, or null when it misses. */
export function raySlab3(origin: Vec3, direction: Vec3, box: Box3): number | null {
  let near = -Infinity;
  let far = Infinity;

  for (const axis of ["x", "y", "z"] as const) {
    const o = origin[axis];
    const d = direction[axis];
    const lo = box.min[axis];
    const hi = box.max[axis];

    if (Math.abs(d) < 1e-8) {
      // Parallel to this pair of slabs: a miss unless it starts between them.
      if (o < lo || o > hi) return null;
      continue;
    }
    let t0 = (lo - o) / d;
    let t1 = (hi - o) / d;
    if (t0 > t1) [t0, t1] = [t1, t0];
    near = Math.max(near, t0);
    far = Math.min(far, t1);
    if (near > far) return null;
  }
  if (far < 0) return null;
  return near >= 0 ? near : 0;
}
